/**
 * Map between Vue Router locations and project-relative file identities.
 * Route params use encodeURIComponent; disk paths stay unencoded.
 */
import {
  displayFileName,
  documentFileName,
  hasMapExtension,
  stripDocumentExtension,
} from "../lib/fileNames";
import {
  isNothing,
  just,
  nothing,
  unwrapMaybeOr,
  type Maybe,
} from "../lib/maybe";
import {
  chapterFolderFromScenePath,
  sceneFileFromScenePath,
  scenePath,
} from "../lib/zwConfig";

export type EditorSection =
  "manuscript" | "storyboard" | "notes" | "database" | "trash";

export const DEFAULT_SECTION_ROUTES: Record<EditorSection, string> = {
  manuscript: "/",
  storyboard: "/storyboard",
  notes: "/notes",
  database: "/database",
  trash: "/trash",
};

/** Strip the document extension for prettier manuscript scene segments. */
export function sceneRouteSegment(sceneFile: string): string {
  return stripDocumentExtension(sceneFile);
}

/** Re-add the document extension (`.zwdoc`) when the segment omitted it. */
export function sceneFileFromRouteSegment(segment: string): string {
  return documentFileName(segment);
}

export function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

export function decodeSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** `Chapters/<folder>/<sceneFile>` → `/<folder>/<sceneStem>` location path. */
export function manuscriptPathFromRelative(
  relativePath: string,
): Maybe<string> {
  const folder = chapterFolderFromScenePath(relativePath);
  const sceneFile = sceneFileFromScenePath(relativePath);
  if (isNothing(folder) || isNothing(sceneFile)) return nothing;
  return just(
    `/${encodeSegment(folder.value)}/${encodeSegment(sceneRouteSegment(sceneFile.value))}`,
  );
}

/** Route params → `Chapters/<folder>/<sceneFile>`. */
export function relativeFromManuscriptParams(
  chapter: string,
  scene: string,
): string {
  const folder = decodeSegment(chapter);
  const sceneFile = sceneFileFromRouteSegment(decodeSegment(scene));
  return scenePath(folder, sceneFile);
}

/**
 * Manuscript map figure leaf → `/map/<encoded mapPath>` (project-relative
 * `.zwmap`, usually `Database/<name>.zwmap`). Distinct from `/database/map/:name`
 * (worldbuilding editor).
 */
export function manuscriptMapPath(mapPath: string): string {
  const normalized = mapPath.replace(/\\/g, "/").replace(/^\//, "");
  if (normalized.length === 0) return "/";
  return `/map/${encodeSegment(normalized)}`;
}

/** Route param → project-relative map path (`Database/World.zwmap`). */
export function mapPathFromRouteParam(param: string): string {
  return decodeSegment(param).replace(/\\/g, "/").replace(/^\//, "");
}

export function notesPath(name: Maybe<string>): string {
  if (isNothing(name) || name.value.length === 0) return "/notes";
  return `/notes/${encodeSegment(name.value)}`;
}

export function trashPath(name: Maybe<string>): string {
  if (isNothing(name) || name.value.length === 0) return "/trash";
  return `/trash/${encodeSegment(name.value)}`;
}

export function databaseListPath(): string {
  return "/database";
}

export function databaseGraphPath(): string {
  return "/database/graph";
}

/** Entry (`.zwdoc`) or map (`.zwmap`) → typed database route. */
export function databaseResourcePath(name: string): string {
  const decoded = name;
  if (hasMapExtension(decoded)) {
    const stem = decoded.replace(/\.zwmap$/i, "");
    return `/database/map/${encodeSegment(stem)}`;
  }
  return `/database/entry/${encodeSegment(stripDocumentExtension(decoded))}`;
}

export function databaseEntryFileName(routeName: string): string {
  return documentFileName(decodeSegment(routeName));
}

export function databaseMapFileName(routeName: string): string {
  const base = decodeSegment(routeName);
  return hasMapExtension(base) ? base : `${base}.zwmap`;
}

export function noteNameFromRouteParam(param: string): string {
  return decodeSegment(param);
}

export function trashNameFromRouteParam(param: string): string {
  return decodeSegment(param);
}

/** Best-effort section for sticky nav / lastRouteBySection. */
export function sectionFromPath(path: string): Maybe<EditorSection> {
  const bare = path.split("?")[0] ?? "/";
  if (bare === "/" || bare === "") return just("manuscript");
  if (bare === "/map" || bare.startsWith("/map/")) return just("manuscript");
  if (bare === "/storyboard" || bare.startsWith("/storyboard/")) {
    return just("storyboard");
  }
  if (bare === "/notes" || bare.startsWith("/notes/")) return just("notes");
  if (bare === "/trash" || bare.startsWith("/trash/")) return just("trash");
  if (bare === "/database" || bare.startsWith("/database/"))
    return just("database");
  // `/:chapter/:scene`
  if (/^\/[^/]+\/[^/]+\/?$/.test(bare)) return just("manuscript");
  return nothing;
}

/** Map legacy `activeTab` values to a default hash path. */
export function pathFromLegacyActiveTab(tab: Maybe<string>): string {
  if (isNothing(tab)) return "/";
  switch (tab.value) {
    case "storyboard":
      return "/storyboard";
    case "notes":
      return "/notes";
    case "database":
      return "/database";
    case "trash":
      return "/trash";
    case "manuscript":
    default:
      return "/";
  }
}

export function displayChapterFromRelative(relativePath: string): string {
  return unwrapMaybeOr(
    chapterFolderFromScenePath(relativePath),
    displayFileName(relativePath),
  );
}
